import React, { useCallback, useEffect, useRef, useState } from "react";
import { blackboardTheme, kuroirTheme } from "./themes";
import Editor from "@monaco-editor/react";

const SitePilotAddonsEditor = ({
  setEditorValue,
  editorValue,
  singleMailTemplate,
}) => {
  const editorRef = useRef(null);

  const [fontSize, setFontSize] = useState(12);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [copied, setCopied] = useState(false);
  const [theme, setTheme] = useState("kuroir");
  const [openPreview, setOpenPreview] = useState(false);
  const [isFullScreenMode, setIsFullScreenMode] = useState(false);

  /* ===================== Stats ===================== */
  const [stats, setStats] = useState({
    line: 1,
    column: 1,
    lines: 1,
  });

  /* ===================== Editor Mount ===================== */
  const handleEditorDidMount = useCallback((editor, monaco) => {
    editorRef.current = editor;

    monaco.editor.defineTheme("kuroir", kuroirTheme);
    monaco.editor.defineTheme("blackboard", blackboardTheme);
    monaco.editor.setTheme(theme);

    const updateStats = () => {
      const position = editor.getPosition();
      const model = editor.getModel();

      setStats({
        line: position.lineNumber,
        column: position.column,
        lines: model.getLineCount(),
      });
    };

    const updateUndoRedo = () => {
      const model = editor.getModel();
      if (model) {
        setCanUndo(model.canUndo());
        setCanRedo(model.canRedo());
      }
    };

    updateStats();
    updateUndoRedo();

    editor.onDidChangeCursorPosition(updateStats);
    editor.onDidChangeModelContent(() => {
      updateStats();
      updateUndoRedo();
    });
  }, []);

  /* ===================== Theme Change ===================== */
  useEffect(() => {
    if (window.monaco) {
      window.monaco.editor.setTheme(theme);
    }
  }, [theme]);

  /* ===================== Load API Code ===================== */
  useEffect(() => {
    if (singleMailTemplate?.code) {
      setEditorValue(singleMailTemplate.code);
    }
  }, [singleMailTemplate]);

  /* ===================== Editor Actions ===================== */
  const toggleTheme = () => {
    setTheme((prev) => (prev === "kuroir" ? "blackboard" : "kuroir"));
  };

  const handleZoomIn = () => setFontSize((p) => Math.min(p + 2, 32));
  const handleZoomOut = () => setFontSize((p) => Math.max(p - 2, 8));

  const handleUndo = () => editorRef.current?.trigger("keyboard", "undo", null);
  const handleRedo = () => editorRef.current?.trigger("keyboard", "redo", null);

  const handleCopy = async () => {
    const editor = editorRef.current;
    if (!editor) return;

    const model = editor.getModel();
    const selection = editor.getSelection();
    const text =
      selection && !selection.isEmpty()
        ? model.getValueInRange(selection)
        : model.getValue();

    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  /* ===================== Render ===================== */
  return (
    <>
      <div className={isFullScreenMode ? "full-screen-component p-0" : ""}>
        <div
          className="overflow-hidden"
          style={{
            height: isFullScreenMode ? "100vh" : "300px",
          }}
        >
          {/* ===================== Toolbar ===================== */}
          <div
            className="justify-content-between toolbar border-bottom hstack"
            style={{ height: 36 }}
          >
            <div className="hstack gap-1">
              {/* Undo */}
              <div className="p-2 border-end">
                <i
                  title="Undo"
                  className="fa-solid fa-reply CP"
                  onClick={canUndo ? handleUndo : undefined}
                  style={{ opacity: canUndo ? 1 : 0.4 }}
                />
              </div>

              {/* Redo */}
              <div className="p-2 border-end">
                <i
                  title="Redo"
                  className="fa-solid fa-share CP"
                  onClick={canRedo ? handleRedo : undefined}
                  style={{ opacity: canRedo ? 1 : 0.4 }}
                />
              </div>

              {/* Copy */}
              <div className="p-2 border-end">
                <i
                  title="Copy"
                  className={`fa-solid ${
                    copied ? "fa-circle-check" : "fa-copy"
                  } CP`}
                  onClick={handleCopy}
                />
                {copied && <span className="ms-1">Copied</span>}
              </div>

              {/* Zoom */}
              <div className="p-2 border-end">
                <i
                  title="Zoom In"
                  className="fa-solid fa-plus CP"
                  onClick={handleZoomIn}
                />
              </div>
              <div className="p-2 border-end">
                <i
                  title="Zoom Out"
                  className="fa-solid fa-minus CP"
                  onClick={handleZoomOut}
                />
              </div>

              {/* Fullscreen */}
              <div className="p-2 border-end">
                <i
                  title={isFullScreenMode ? "Exit Fullscreen" : "Fullscreen"}
                  className={`fa-solid ${
                    isFullScreenMode ? "fa-minimize" : "fa-maximize"
                  } CP`}
                  onClick={() => setIsFullScreenMode(!isFullScreenMode)}
                />
              </div>

              {/* Theme */}
              <div className="p-2 border-end">
                <i
                  title="Toggle Theme"
                  className={`fa-solid ${
                    theme === "kuroir" ? "fa-sun" : "fa-moon"
                  } CP`}
                  onClick={toggleTheme}
                />
              </div>

              {/* View Preview */}
              {editorValue &&
              <div className="p-2 border-end">
                <i
                  title="View Preview"
                  className={`fa-solid ${openPreview ? "fa-eye-slash" : "fa-eye"} CP`}
                  onClick={() => setOpenPreview(!openPreview)}
                />
              </div>
              }
            </div>

            {/* Stats */}
            <div className="hstack gap-1 pe-2" style={{ fontSize: 12 }}>
              <span>Line: {stats.line}</span>,<span>Col: {stats.column}</span>,
              <span>Lines: {stats.lines}</span>
            </div>
          </div>

          {/* ===================== Editor ===================== */}
          {openPreview ? (
            <iframe
              title="Template Preview"
              className="w-100 h-100 border-0"
              srcDoc={editorValue}
            ></iframe>
          ) : (
            <Editor
              theme={theme}
              defaultLanguage="html"
              value={editorValue}
              onChange={(val) => setEditorValue(val || "")}
              onMount={handleEditorDidMount}
              options={{
                fontSize,
                wordWrap: "on",
                minimap: { enabled: false },
                automaticLayout: true,
              }}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default SitePilotAddonsEditor;
